/**
 * App-wide semantic color tokens and status helpers.
 *
 * Brand roles: action = primary CTA fill; chrome = shell (dark navy);
 * highlight = orange marker; accent = focus/selection; subtle = wash. Prefer
 * `--mt-brand-action*` over legacy `--mt-brand-primary*` aliases.
 *
 * Hue mappings start as compatibility aliases of current Mantine shades so
 * consumers can migrate off raw `orange` / `green` names.
 *
 * ## Collaborator hue retune checklist
 * Update shades only in `SEMANTIC_CSS_VARIABLES` and the helper return values
 * in this file. Then visually verify:
 * 1. Job list + project list (shared lifecycle colors)
 * 2. Invoice detail (draft / void / invoiced)
 * 3. Dispatch board lanes + attention cards
 * 4. Assign driver modal (attention copy vs brand CTA)
 * 5. Equipment deployment badges + fulfillment identity headers
 * 6. Users list (active / deactivated)
 * 7. Dashboard fleet series (no brand / orange / red series)
 * 8. Dark navbar chrome vs primary filled CTA (same family; accent for selection)
 * 9. Project details Jobs section (schedule rail/badge vs worker/equipment identity)
 *
 * Do not scatter new hex literals in components after migration.
 */

export type MantineColorName = string

/** Lifecycle statuses shared by jobs (and projects where applicable). */
export type LifecycleStatus = 'estimate' | 'in_progress' | 'closed' | 'canceled'

export type DeploymentStatusColor =
    | 'available'
    | 'deployed'
    | 'picked_up'
    | 'dispatched'
    | 'unknown'

export type FulfillmentStatusColor = 'met' | 'under' | 'over'

export type ConfidenceColorLevel = 'unconfirmed' | 'ready' | 'committed' | 'blocked'

export type AvailabilityDayStatus = 'unassigned' | 'bounded' | 'open_ended'

/** Strip encoding: booking aid (free/busy) vs resource calendar (idle/on-a-job). */
export type AssignmentStripPresentation = 'availability' | 'schedule'

export type CommitmentBand = 'on_site' | 'awaiting_deployment' | 'free'

export type AlertTone = 'danger' | 'warning' | 'success' | 'info'

/** Quote lifecycle statuses — colors live here so badges share one map. */
export type QuoteStatusColor = 'draft' | 'sent' | 'accepted' | 'rejected' | 'voided'

/** Schedule change-preview delta badges. */
export type ChangePreviewDelta = 'added' | 'updated' | 'removed' | 'kept' | 'unchanged'

/** Why a previewed job was left alone. */
export type ChangePreviewReason = 'worked' | 'past' | 'skipped' | 'already_current' | 'unaffected'

/** Stage of a service schedule build-out, used for the preview timeline rail. */
export type ScheduleStage = 'deploy' | 'service' | 'pickup'

/**
 * CSS custom properties emitted by `cssVariablesResolver`.
 * Values reference Mantine palette vars (compatibility map).
 */
export const SEMANTIC_CSS_VARIABLES: Record<string, string> = {
    // Brand — roles: action (CTA), chrome (shell), accent (focus/selection), subtle (wash)
    '--mt-brand-action': 'var(--mantine-color-brand-filled)',
    '--mt-brand-action-hover': 'var(--mantine-color-brand-7)',
    '--mt-brand-on-action': 'var(--mantine-color-white)',
    '--mt-brand-chrome': 'var(--mantine-color-brand-7)',
    '--mt-brand-chrome-hover': 'rgba(255, 255, 255, 0.07)',
    '--mt-brand-chrome-active': 'var(--mantine-color-brand-6)',
    '--mt-brand-chrome-divider': 'rgba(255, 255, 255, 0.14)',
    // 3.0:1 contrast on white; use on chrome backgrounds
    '--mt-brand-highlight': 'var(--mantine-color-highlight-6)',
    '--mt-brand-on-chrome': 'var(--mantine-color-white)',
    '--mt-brand-on-chrome-strong': 'var(--mantine-color-white)',
    '--mt-brand-on-chrome-active': 'var(--mantine-color-white)',
    '--mt-brand-accent': 'var(--mantine-color-brand-5)',
    '--mt-brand-subtle': 'var(--mantine-color-brand-0)',
    '--mt-brand-border': 'var(--mantine-color-brand-4)',
    // Aliases — prefer action tokens; keep primary names for existing consumers
    '--mt-brand-primary': 'var(--mt-brand-action)',
    '--mt-brand-primary-hover': 'var(--mt-brand-action-hover)',
    '--mt-brand-on-primary': 'var(--mt-brand-on-action)',

    // Signal — attention (compat: orange)
    '--mt-signal-attention': 'var(--mantine-color-orange-6)',
    '--mt-signal-attention-0': 'var(--mantine-color-orange-0)',
    '--mt-signal-attention-1': 'var(--mantine-color-orange-1)',
    '--mt-signal-attention-2': 'var(--mantine-color-orange-2)',
    '--mt-signal-attention-3': 'var(--mantine-color-orange-3)',
    '--mt-signal-attention-4': 'var(--mantine-color-orange-4)',
    '--mt-signal-attention-5': 'var(--mantine-color-orange-5)',
    '--mt-signal-attention-7': 'var(--mantine-color-orange-7)',
    '--mt-signal-attention-8': 'var(--mantine-color-orange-8)',
    '--mt-signal-attention-9': 'var(--mantine-color-orange-9)',

    // Signal — pending (compat: yellow)
    '--mt-signal-pending': 'var(--mantine-color-yellow-6)',
    '--mt-signal-pending-0': 'var(--mantine-color-yellow-0)',
    '--mt-signal-pending-1': 'var(--mantine-color-yellow-1)',
    '--mt-signal-pending-2': 'var(--mantine-color-yellow-2)',
    '--mt-signal-pending-4': 'var(--mantine-color-yellow-4)',
    '--mt-signal-pending-5': 'var(--mantine-color-yellow-5)',
    '--mt-signal-pending-9': 'var(--mantine-color-yellow-9)',

    // Signal — positive (compat: green)
    '--mt-signal-positive': 'var(--mantine-color-green-6)',
    '--mt-signal-positive-0': 'var(--mantine-color-green-0)',
    '--mt-signal-positive-2': 'var(--mantine-color-green-2)',
    '--mt-signal-positive-3': 'var(--mantine-color-green-3)',
    '--mt-signal-positive-5': 'var(--mantine-color-green-5)',
    '--mt-signal-positive-7': 'var(--mantine-color-green-7)',
    '--mt-signal-positive-8': 'var(--mantine-color-green-8)',
    '--mt-signal-positive-9': 'var(--mantine-color-green-9)',

    // Signal — critical (compat: red)
    '--mt-signal-critical': 'var(--mantine-color-red-6)',
    '--mt-signal-critical-filled': 'var(--mantine-color-red-filled)',

    // Signal — info (compat: blue)
    '--mt-signal-info': 'var(--mantine-color-blue-6)',
    '--mt-signal-info-0': 'var(--mantine-color-blue-0)',
    '--mt-signal-info-5': 'var(--mantine-color-blue-5)',
    '--mt-signal-info-6': 'var(--mantine-color-blue-6)',

    // Structural
    '--mt-surface-page': 'var(--mantine-color-body)',
    '--mt-surface-raised': 'var(--mantine-color-white)',
    '--mt-surface-subtle': 'var(--mantine-color-gray-0)',
    '--mt-border-default': 'var(--mantine-color-gray-3)',
    '--mt-border-strong': 'var(--mantine-color-gray-4)',
    '--mt-text-muted': 'var(--mantine-color-dimmed)',

    // Availability — free vs busy only for users. Jobs with no end date still count as
    // busy; open_ended is an internal schedule detail, not a third strip state.
    '--mt-availability-free': 'var(--mantine-color-green-6)',
    '--mt-availability-busy': 'var(--mantine-color-gray-5)',
    '--mt-availability-open': 'var(--mantine-color-gray-5)',

    // Schedule — quiet empty vs stronger on-a-job (detail / planned strips; not free-green)
    '--mt-schedule-empty': 'var(--mantine-color-gray-2)',
    '--mt-schedule-loaded': 'var(--mantine-color-gray-6)',

    // Stock commitments — out with no known return, waiting to deploy, free capacity
    '--mt-commitment-on-site': 'var(--mantine-color-orange-8)',
    '--mt-commitment-awaiting-deployment': 'var(--mantine-color-gray-6)',
    '--mt-commitment-free': 'var(--mantine-color-gray-2)',

    // Utilization — dashboard idle-as-figure (stronger than leftover gray wash; not free-green)
    '--mt-utilization-idle': 'var(--mantine-color-gray-6)',

    // Identity
    '--mt-identity-worker': 'var(--mantine-color-cyan-6)',
    '--mt-identity-worker-subtle': 'var(--mantine-color-cyan-0)',
    '--mt-identity-equipment': 'var(--mantine-color-violet-6)',
    '--mt-identity-equipment-subtle': 'var(--mantine-color-violet-0)',
    '--mt-identity-schedule': 'var(--mantine-color-indigo-6)',
    '--mt-identity-schedule-subtle': 'var(--mantine-color-indigo-2)',

    // Focus (distinct from brand filled and signals)
    '--mt-focus-ring': 'var(--mantine-color-blue-5)',

    // Board — currently-active assigned bar (compat teal; not a chart-series or attention hue)
    '--mt-board-active-assigned': 'var(--mantine-color-teal-3)',
    '--mt-board-active-assigned-text': 'var(--mantine-color-teal-9)',
}

/** Canonical job lifecycle map — projects MUST use the same values. */
export function lifecycleStatusColor(status: LifecycleStatus): MantineColorName {
    switch (status) {
        case 'estimate':
            return 'blue'
        case 'in_progress':
            return 'green'
        case 'closed':
            return 'gray'
        case 'canceled':
            return 'red'
    }
}

/** Shared display labels for job and project lifecycle badges and filters. */
export function lifecycleStatusLabel(status: LifecycleStatus): string {
    switch (status) {
        case 'estimate':
            return 'Estimate'
        case 'in_progress':
            return 'Active'
        case 'closed':
            return 'Closed'
        case 'canceled':
            return 'Canceled'
    }
}

export function deploymentStatusColor(status: DeploymentStatusColor): MantineColorName {
    switch (status) {
        case 'available':
            return 'gray'
        case 'deployed':
            return 'green'
        case 'picked_up':
        case 'dispatched':
            return 'yellow'
        case 'unknown':
            return 'orange'
    }
}

export function fulfillmentStatusColor(
    status: FulfillmentStatusColor,
): MantineColorName | undefined {
    switch (status) {
        case 'met':
            return 'green'
        case 'under':
            return 'yellow'
        case 'over':
            return undefined
    }
}

export function userAccountStatusColor(active: boolean): MantineColorName {
    return active ? 'green' : 'gray'
}

export function invoiceStatusColor(args: {isVoided: boolean; isDraft: boolean}): MantineColorName {
    if (args.isVoided) {
        return 'gray'
    }
    if (args.isDraft) {
        return 'yellow'
    }
    return 'green'
}

export function quoteStatusColor(status: QuoteStatusColor): MantineColorName {
    switch (status) {
        case 'draft':
            return 'yellow'
        case 'sent':
            return 'blue'
        case 'accepted':
            return 'green'
        case 'rejected':
            return 'red'
        case 'voided':
            return 'gray'
    }
}

export function changePreviewDeltaColor(delta: ChangePreviewDelta): MantineColorName {
    switch (delta) {
        case 'added':
            return 'green'
        case 'updated':
            return 'blue'
        case 'removed':
            return 'red'
        case 'kept':
            return 'orange'
        case 'unchanged':
            return 'gray'
    }
}

/** A visit the preview leaves alone — all quiet by design. */
export function changePreviewReasonColor(_reason: ChangePreviewReason): MantineColorName {
    void _reason
    return 'gray'
}

/** Preview timeline rail — the schedule's own hue for service, anchors distinct from it. */
export function scheduleStageColor(stage: ScheduleStage): MantineColorName {
    switch (stage) {
        case 'deploy':
            return 'blue.6'
        case 'service':
            return 'indigo.6'
        case 'pickup':
            return 'teal.6'
    }
}

/** Resource picker “fit” badges — neutral, not availability-free green. */
export function resourceFitBadgeColor(): MantineColorName {
    return 'gray'
}

export function confidenceStatusColor(confidence: ConfidenceColorLevel): MantineColorName {
    switch (confidence) {
        case 'unconfirmed':
        case 'ready':
            return 'gray'
        case 'committed':
            return 'blue'
        case 'blocked':
            return 'red'
    }
}

export function availabilityDayColor(status: AvailabilityDayStatus): MantineColorName {
    switch (status) {
        case 'unassigned':
            return 'green.6'
        case 'bounded':
        case 'open_ended':
            // No end date is still "busy" for picking — not a separate user-facing state.
            return 'gray.5'
    }
}

/** Quiet empty vs stronger loaded — for schedule-mode strips (not availability-free green). */
export function scheduleDayColor(status: AvailabilityDayStatus): MantineColorName {
    switch (status) {
        case 'unassigned':
            return 'gray.2'
        case 'bounded':
        case 'open_ended':
            return 'gray.6'
    }
}

export function assignmentStripDayColor(
    presentation: AssignmentStripPresentation,
    status: AvailabilityDayStatus,
): MantineColorName {
    return presentation === 'schedule' ? scheduleDayColor(status) : availabilityDayColor(status)
}

/** Dashboard unused segment — idle-as-figure, not availability-free green. */
export function utilizationIdleColor(): MantineColorName {
    return 'gray.6'
}

export function identityWorkerColor(): MantineColorName {
    return 'cyan'
}

export function identityEquipmentColor(): MantineColorName {
    return 'violet'
}

export function identityWorkerSubtle(): MantineColorName {
    return 'cyan.0'
}

export function identityEquipmentSubtle(): MantineColorName {
    return 'violet.0'
}

export function identityScheduleColor(): MantineColorName {
    return 'indigo'
}

export function identityScheduleSubtle(): MantineColorName {
    return 'indigo.2'
}

export function identityProgressColor(kind: 'worker' | 'rental_asset'): MantineColorName {
    return kind === 'worker' ? identityWorkerColor() : identityEquipmentColor()
}

export function identityHeaderSubtle(kind: 'worker' | 'rental_asset'): MantineColorName {
    return kind === 'worker' ? identityWorkerSubtle() : identityEquipmentSubtle()
}

/** Unfilled requirement progress — attention, not identity. */
export function fulfillmentUnfilledProgressColor(): MantineColorName {
    return 'orange.4'
}

export function alertToneColor(tone: AlertTone): MantineColorName {
    switch (tone) {
        case 'danger':
            return 'red'
        case 'warning':
            return 'yellow'
        case 'success':
            return 'green'
        case 'info':
            return 'blue'
    }
}

/** Dispatch-style attention alerts (compat orange; distinct from soft yellow warning). */
export function signalAttentionColor(): MantineColorName {
    return 'orange'
}

export function actionDestructiveColor(): MantineColorName {
    return 'red'
}

export function signalPositiveTextColor(): MantineColorName {
    return 'green.7'
}

export function signalAttentionTextColor(): MantineColorName {
    return 'orange.8'
}

export function signalAttentionHeadingColor(): MantineColorName {
    return 'orange.9'
}

export function signalAttentionActionColor(): MantineColorName {
    return 'orange.7'
}

export function signalInfoLinkColor(): MantineColorName {
    return 'blue'
}

export function commitmentBandColor(band: CommitmentBand): MantineColorName {
    switch (band) {
        case 'on_site':
            return 'orange.8'
        case 'awaiting_deployment':
            return 'gray.6'
        case 'free':
            return 'gray.2'
    }
}

/** Analytics trend — aligned with signal-positive / signal-critical (not a separate teal). */
export function trendUpColor(): MantineColorName {
    return 'green'
}

export function trendDownColor(): MantineColorName {
    return 'red'
}

/**
 * Categorical chart series — excludes brand, red, orange, and reserved
 * success/active greens used as operational signals.
 */
export const CHART_SERIES_COLORS: readonly MantineColorName[] = [
    'indigo',
    'teal',
    'grape',
    'cyan',
    'violet',
    'pink',
    'lime',
    'dark',
] as const

export function chartSeriesColor(index: number): MantineColorName {
    const color = CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]
    return color ?? 'indigo'
}
